import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Badge,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Work as WorkIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNotifications } from '../contexts/NotificationContext';
import { Notification } from '../lib/api/notifications';

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, error, refreshNotifications, markAsRead, markAllAsRead } = useNotifications();
  
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null);

  useEffect(() => {
    refreshNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleFilterChange = (newFilter: 'all' | 'unread') => {
    setFilter(newFilter);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notificationId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedNotification(notificationId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
    handleMenuClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_project':
        return <WorkIcon sx={{ color: '#ff6b6b' }} />;
      case 'project_approved':
        return <CheckCircleIcon sx={{ color: '#28a745' }} />;
      case 'offer_received':
      case 'offer_accepted':
        return <NotificationsActiveIcon sx={{ color: '#007bff' }} />;
      case 'review_received':
        return <StarIcon sx={{ color: '#ffc107' }} />;
      case 'credit_purchase':
        return <CreditCardIcon sx={{ color: '#17a2b8' }} />;
      case 'account_approved':
        return <PersonIcon sx={{ color: '#28a745' }} />;
      default:
        return <NotificationsIcon sx={{ color: '#6c757d' }} />;
    }
  };

  const getNotificationTypeText = (type: string) => {
    switch (type) {
      case 'new_project':
        return t('notifications.types.newProject');
      case 'project_approved':
        return t('notifications.types.projectApproved');
      case 'offer_received':
        return t('notifications.types.offerReceived');
      case 'offer_accepted':
        return t('notifications.types.offerAccepted');
      case 'review_received':
        return t('notifications.types.reviewReceived');
      case 'credit_purchase':
        return t('notifications.types.creditPurchase');
      case 'account_approved':
        return t('notifications.types.accountApproved');
      default:
        return t('notifications.types.notification');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.read_at) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.data?.project_id) {
      navigate(`/project/${notification.data.project_id}`);
    } else if (notification.type === 'credit_purchase') {
      navigate('/credits');
    } else if (notification.type === 'account_approved') {
      navigate('/profile');
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') {
      return !notification.read_at;
    }
    return true;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return t('notifications.timeAgo.justNow');
    } else if (diffInHours < 24) {
      return t('notifications.timeAgo.hoursAgo', { hours: diffInHours });
    } else if (diffInHours < 48) {
      return t('notifications.timeAgo.yesterday');
    } else {
      return date.toLocaleDateString('bg-BG');
    }
  };

  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress sx={{ color: '#28A0F0' }} />
          </Box>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 700, 
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {t('notifications.title')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Button
              variant={filter === 'all' ? 'contained' : 'outlined'}
              onClick={() => handleFilterChange('all')}
              sx={{
                backgroundColor: filter === 'all' ? '#28A0F0' : 'transparent',
                color: filter === 'all' ? '#ffffff' : '#28A0F0',
                borderColor: '#28A0F0',
                '&:hover': {
                  backgroundColor: filter === 'all' ? '#1d4ed8' : 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              {t('notifications.all')} ({notifications.length})
            </Button>
            <Button
              variant={filter === 'unread' ? 'contained' : 'outlined'}
              onClick={() => handleFilterChange('unread')}
              sx={{
                backgroundColor: filter === 'unread' ? '#28A0F0' : 'transparent',
                color: filter === 'unread' ? '#ffffff' : '#28A0F0',
                borderColor: '#28A0F0',
                '&:hover': {
                  backgroundColor: filter === 'unread' ? '#1d4ed8' : 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              {t('notifications.unread')} ({unreadCount})
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="outlined"
                onClick={handleMarkAllAsRead}
                startIcon={<MarkEmailReadIcon />}
                sx={{
                  color: '#28a745',
                  borderColor: '#28a745',
                  '&:hover': {
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                  },
                }}
              >
                {t('notifications.markAllAsRead')}
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#ffffff' }}>
            {error}
          </Alert>
        )}

        {filteredNotifications.length === 0 ? (
          <Card sx={{ backgroundColor: '#1a1a2e', border: '1px solid #2d2d44' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <NotificationsIcon sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                {filter === 'unread' ? t('notifications.noUnreadNotifications') : t('notifications.noNotifications')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {filter === 'unread' 
                  ? t('notifications.allReadDesc')
                  : t('notifications.noNotificationsDesc')
                }
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List sx={{ backgroundColor: '#1a1a2e', borderRadius: 2, border: '1px solid #2d2d44' }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    backgroundColor: notification.read_at ? 'transparent' : 'rgba(40, 160, 240, 0.05)',
                    borderLeft: notification.read_at ? 'none' : '4px solid #28A0F0',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                    },
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    {getNotificationIcon(notification.type)}
                  </Box>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#ffffff',
                            fontWeight: notification.read_at ? 400 : 600,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={getNotificationTypeText(notification.type)}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(40, 160, 240, 0.2)',
                            color: '#28A0F0',
                            fontSize: '0.75rem',
                          }}
                        />
                        {!notification.read_at && (
                          <Badge
                            variant="dot"
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: '#28A0F0',
                              },
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a0a0a0',
                            mb: 0.5,
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#6c757d',
                          }}
                        >
                          {formatDate(notification.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, notification.id);
                      }}
                      sx={{ color: '#a0a0a0' }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                
                {index < filteredNotifications.length - 1 && (
                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Container>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid #2d2d44',
            '& .MuiMenuItem-root': {
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => selectedNotification && handleMarkAsRead(selectedNotification)}>
          <CheckCircleIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('notifications.markAsRead')}
        </MenuItem>
      </Menu>

      <Footer />
    </Box>
  );
};

export default NotificationsPage;
